/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, where, onSnapshot, doc, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { type Office, type Organization, type Department } from '@/types/org';
import { logger } from '@/lib/logger';

interface OrganizationContextType {
    organization: Organization | null;
    offices: Office[];
    departments: Department[]; // Added
    activeOfficeId: string | null;
    activeOffice?: Office;
    activeDepartmentId: string | null; // Added
    activeDepartment?: Department;
    setActiveOfficeId: (id: string | null) => void;
    setActiveDepartmentId: (id: string | null) => void; // Added
    /** Re-read offices from Firestore (e.g. after create) when the snapshot lags behind local writes. */
    refreshOffices: () => Promise<void>;
    refreshDepartments: () => Promise<void>;
    loading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { profile } = useAuth();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [offices, setOffices] = useState<Office[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [activeOfficeId, setActiveOfficeId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Retry counter — incremented on permission-denied so the effect re-fires
    // after a delay. Resets whenever the orgId changes.
    const [retryTick, setRetryTick] = useState(0);
    const retryCountRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Explicit Department Switching
    const [manualDepartmentId, setManualDepartmentId] = useState<string | null>(null);
    // Default to NULL (Global Office View) instead of profile.departmentId
    const activeDepartmentId = manualDepartmentId;

    // Reset retry state whenever the org changes
    useEffect(() => {
        retryCountRef.current = 0;
        setRetryTick(0);
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    }, [profile?.orgId]);

    useEffect(() => {
        if (!profile?.orgId) {
            setLoading(false);
            return;
        }

        const MAX_RETRIES = 4;

        // Schedule a retry after a permission-denied error.
        // Exponential back-off: 500 ms, 1 s, 2 s, 4 s.
        const scheduleRetry = (label: string, err: Error) => {
            logger.error(`OrganizationContext ${label} snapshot:`, err);
            const code = (err as { code?: string }).code;
            if (code === 'permission-denied' && retryCountRef.current < MAX_RETRIES) {
                const delay = 500 * Math.pow(2, retryCountRef.current);
                retryCountRef.current += 1;
                retryTimerRef.current = setTimeout(() => setRetryTick(n => n + 1), delay);
            }
        };

        // Listen to organization doc
        const unsubscribeOrg = onSnapshot(
            doc(db, 'organizations', profile.orgId),
            (snap) => {
                if (snap.exists()) {
                    setOrganization({ ...snap.data(), id: snap.id } as Organization);
                }
            },
            (err) => scheduleRetry('org', err),
        );

        // Listen to offices
        const q = query(collection(db, 'organizations', profile.orgId, 'offices'), where('orgId', '==', profile.orgId));
        const unsubscribeOffices = onSnapshot(
            q,
            (snap) => {
                const officesList = snap.docs.map(d => ({ ...d.data(), id: d.id } as Office));
                setOffices(officesList);
            },
            (err) => scheduleRetry('offices', err),
        );

        const qDepts = query(collection(db, 'organizations', profile.orgId, 'departments'), where('orgId', '==', profile.orgId));
        const unsubscribeDepts = onSnapshot(
            qDepts,
            (snap) => {
                const deptsList = snap.docs.map(d => ({ ...d.data(), id: d.id } as Department));
                setDepartments(deptsList);
                setLoading(false);
            },
            (err) => scheduleRetry('departments', err),
        );

        return () => {
            unsubscribeOrg();
            unsubscribeOffices();
            unsubscribeDepts();
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        };
    // retryTick intentionally included so permission-denied errors trigger a re-subscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.orgId, retryTick]);

    const activeOffice = useMemo(() => offices.find(o => o.id === activeOfficeId), [offices, activeOfficeId]);
    const activeDepartment = useMemo(() => departments.find(d => d.id === activeDepartmentId), [departments, activeDepartmentId]);

    const setActiveDepartmentId = useCallback((id: string | null) => {
        setManualDepartmentId(id);
    }, []);

    const stableSetActiveOfficeId = useCallback((id: string | null) => {
        setActiveOfficeId(id);
    }, []);

    const refreshOffices = useCallback(async () => {
        if (!profile?.orgId) return;
        const qOffices = query(
            collection(db, 'organizations', profile.orgId, 'offices'),
            where('orgId', '==', profile.orgId),
        );
        const snap = await getDocs(qOffices);
        setOffices(snap.docs.map(d => ({ ...d.data(), id: d.id } as Office)));
    }, [profile?.orgId]);

    const refreshDepartments = useCallback(async () => {
        if (!profile?.orgId) return;
        const qDepts = query(
            collection(db, 'organizations', profile.orgId, 'departments'),
            where('orgId', '==', profile.orgId),
        );
        const snap = await getDocs(qDepts);
        setDepartments(snap.docs.map(d => ({ ...d.data(), id: d.id } as Department)));
    }, [profile?.orgId]);

    const value = useMemo(() => ({
        organization,
        offices,
        departments,
        activeOfficeId,
        activeOffice,
        activeDepartmentId,
        activeDepartment,
        setActiveOfficeId: stableSetActiveOfficeId,
        setActiveDepartmentId,
        refreshOffices,
        refreshDepartments,
        loading,
    }), [
        organization, offices, departments,
        activeOfficeId, activeOffice,
        activeDepartmentId, activeDepartment,
        stableSetActiveOfficeId, setActiveDepartmentId,
        refreshOffices, refreshDepartments,
        loading,
    ]);

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};

export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (context === undefined) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
};
